import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { Typography } from 'antd';
import {
  ColumnCardsList,
  ProjectConstructorNavigationControls,
  ProjectsAddColumnForm,
  ProjectsConstructorLayout,
  useNavigateConstructorTabs,
  validateColumnsTab,
} from '@/views';
import { notify, useAppFindFormik } from '@/components';
import { ProjectDto, ColumnTypesEnum } from '@/core';
import classes from './ProjectsColumnsPage.module.scss';

const columnsSettingsMap = new Map([
  [ColumnTypesEnum.Other, { label: 'Other', isDefault: false, isRequired: false }],
  [ColumnTypesEnum.Employee, { label: 'Employees', isDefault: false, isRequired: true }],
  [ColumnTypesEnum.Epic, { label: 'Epics', isDefault: false, isRequired: true }],
  [ColumnTypesEnum.LoggedTime, { label: 'Logged Time', isDefault: false, isRequired: true }],
  [ColumnTypesEnum.CostType, { label: 'Cost Type', isDefault: true, isRequired: false }],
  [ColumnTypesEnum.Product, { label: 'Product', isDefault: true, isRequired: false }],
  [ColumnTypesEnum.Position, { label: 'Position', isDefault: true, isRequired: false }],
  [ColumnTypesEnum.Department, { label: 'Department', isDefault: true, isRequired: false }],
  [ColumnTypesEnum.EpicDescription, { label: 'Epic Description', isDefault: true, isRequired: false }],
  [ColumnTypesEnum.UnitManager, { label: 'Unit Manager', isDefault: true, isRequired: false }],
]);

export const getColumnSettings = (columnType: ColumnTypesEnum) => {
  const columnSettings = columnsSettingsMap.get(columnType);

  if (!columnSettings) throw new Error(`${columnType} is not implemented`);

  return columnSettings;
};

const useRequiredColumnsForms = () => {
  const formEmployee = useAppFindFormik(ColumnTypesEnum.Employee);
  const formLoggedTime = useAppFindFormik(ColumnTypesEnum.LoggedTime);
  const formEpics = useAppFindFormik(ColumnTypesEnum.Epic);
  return [formEmployee, formLoggedTime, formEpics];
};

export const ProjectsColumnsPage = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.constructor' });
  const projectData = useOutletContext<ProjectDto>();
  const isColumnsTabValid = validateColumnsTab(projectData);
  const requiredColumnsForm = useRequiredColumnsForms();
  const { navigateToNextTab, navigateToPreviousTab } = useNavigateConstructorTabs(projectData);
  const userColumns = projectData.columns.filter((column) => !getColumnSettings(column.type).isDefault);
  const isColumnExist = (columnType: string) =>
    projectData.columns.find((column) => column.type === columnType);

  const navigateToTablesPage = () => {
    if (!isColumnsTabValid) {
      requiredColumnsForm.forEach((form) => form.current?.setTouched({ name: true, sourceName: true }));
      notify.error(t('addColumns.invalidPage'));
      return;
    }
    navigateToNextTab();
  };

  return (
    <ProjectsConstructorLayout
      projectData={projectData}
      content={
        <>
          <Typography.Title level={3}>
            {projectData?.name}: {t('addColumns.title')}
          </Typography.Title>
          <Typography.Text type="secondary" className={classes.pageDescription}>
            {t('addColumns.description')}
          </Typography.Text>
          <div className={classes.formsList}>
            {Object.values(ColumnTypesEnum).map((columnType) => {
              if (columnType === ColumnTypesEnum.Other || !isColumnExist(columnType))
                return (
                  <ProjectsAddColumnForm key={columnType} projectData={projectData} columnType={columnType} />
                );
              return null;
            })}
          </div>
          {userColumns?.length ? (
            <ColumnCardsList projectData={projectData} userColumns={userColumns} />
          ) : null}
        </>
      }
      navigationControls={
        <ProjectConstructorNavigationControls
          navigateToPreviousTab={navigateToPreviousTab}
          navigateToNextTab={navigateToTablesPage}
        />
      }
    />
  );
};
